import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import type { W_Item_FindRelatedOrdersTable_VM_Row } from 'ab-api/web/item/findRelatedOrdersTable';

import type ItemVM from 'ab-viewModels/item/edit.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { OrderStatus } from '@acceligentllc/shared/enums/orderStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import type { RootState } from 'af-reducers';

import { downloadCSV } from 'af-utils/csv.utils';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { SegmentLabel } from '@acceligentllc/storybook';

import DateFilter from 'af-components/DateFilter';
import DynamicTooltip from 'af-components/DynamicTooltip';
import TableNew from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import LinkCell from 'af-components/Table/Cells/LinkCell';
import UpdatedByCell from 'af-components/Table/Cells/UpdatedByCell';
import type { TableProps } from 'af-components/Table/types';

import CLIENT from 'af-routes/client';

import * as ItemActions from 'af-actions/item';

import { toCSVData } from './helpers';
import styles from './styles.module.scss';

interface OwnProps {
	item: ItemVM;
}

type Props = ConnectedProps<typeof connector> & OwnProps;

const DATE_OPTIONS = Object.values(TimePeriodRecurrence).filter((item) => item !== TimePeriodRecurrence.ALL);

const OrderStatusLabel: Record<OrderStatus, { label: string; classModifier: string; }> = {
	[OrderStatus.NEW]: { label: 'New', classModifier: styles['order-status--new'] },
	[OrderStatus.IN_PROGRESS]: { label: 'In Progress', classModifier: styles['order-status--in-progress'] },
	[OrderStatus.COMPLETED]: { label: 'Completed', classModifier: styles['order-status--completed'] },
};

const renderSelectedStatus = (cell: CellContext<W_Item_FindRelatedOrdersTable_VM_Row, OrderStatus>) => {

	const option = cell.row.original.status;

	const status = OrderStatusLabel[option] ?? OrderStatusLabel.NEW;
	return (
		<div className={`${styles['order-status']} ${status.classModifier}`}>
			{status.label}
		</div>
	);
};

const RelatedOrdersTable: React.FC<Props> = (props: Props) => {
	const {
		item,
		findForTable,
		companyData,
	} = props;

	const { state: { orgAlias } } = useLocation<{ orgAlias: string; }>();
	const [search] = useSearchParams();

	const incomingStart = search.get('startDate');
	const incomingEnd = search.get('endDate');
	const incomingPeriod = search.get('period') as TimePeriodRecurrence | null;

	const defaultStart = incomingStart
		? TimeUtils.positionDate(new Date(incomingStart), 'start', 'day')
		: TimeUtils.positionDate(new Date(), 'start', 'day');
	const defaultEnd = incomingEnd
		? TimeUtils.positionDate(new Date(incomingEnd), 'end', 'day')
		: TimeUtils.positionDate(new Date(), 'end', 'day');
	const defaultPeriod = incomingPeriod ?? TimePeriodRecurrence.DAILY;

	const tableRef = React.useRef<TableRef<W_Item_FindRelatedOrdersTable_VM_Row>>(null);

	const [startDate, setStartDate] = React.useState(defaultStart);
	const [endDate, setEndDate] = React.useState(defaultEnd);
	const [period, setPeriod] = React.useState(defaultPeriod);

	const fetchRows = React.useCallback(async (tableRequestModel: TableQuery) => {
		const { page, pageSize, filterByText, sortBy } = tableRequestModel;
		const result = await findForTable(
			item.id,
			new TableQuery({ page, pageSize, sortBy, filterByText }),
			TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY),
			TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY)
		);
		return result;
	}, [findForTable, item.id, startDate, endDate]);

	const filterByDate = React.useCallback((_startDate: Date, _endDate: Date) => {
		tableRef.current?.resetPagination();
		setStartDate(TimeUtils.positionDate(_startDate, 'start', 'day'));
		setEndDate(TimeUtils.positionDate(_endDate, 'end', 'day'));
	}, []);

	const changePeriod = React.useCallback((_period: TimePeriodRecurrence, _selected: Date) => {
		let _startDate: Date = _selected;
		let _endDate: Date = _selected;

		switch (_period) {
			case TimePeriodRecurrence.MONTHLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'month'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'month'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.WEEKLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'week'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'week'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.DAILY:
			case TimePeriodRecurrence.CUSTOM:
				_startDate = TimeUtils.positionDate(_selected, 'start', 'day');
				_endDate = TimeUtils.positionDate(_selected, 'end', 'day');
			default:
				break;
		}

		tableRef.current?.resetPagination();
		setStartDate(_startDate);
		setEndDate(_endDate);
		setPeriod(_period);
	}, []);

	const renderAdditionalFilter = React.useCallback(() => {
		return (
			<div className={styles['table-filter']}>
				<DateFilter
					changePeriod={changePeriod}
					endDate={endDate}
					onChange={filterByDate}
					options={DATE_OPTIONS}
					period={period}
					startDate={startDate}
				/>
			</div>
		);
	}, [changePeriod, endDate, filterByDate, period, startDate]);

	const renderLinkCell = React.useCallback(() =>
		(_cell: CellContext<W_Item_FindRelatedOrdersTable_VM_Row, string>) => {
			const url = new URL(CLIENT.COMPANY.ORDER.EDIT(orgAlias, companyData.name, `${_cell.row.original.id}`), location.origin);
			url.searchParams.append('forPrint', 'true');
			return <LinkCell label="" path={url.toString()} />;
		}, [companyData.name, orgAlias]);

	const renderQuantityCell = React.useCallback((cell: CellContext<W_Item_FindRelatedOrdersTable_VM_Row, string>) => {

		const message = <>
			Unfulfilled Quantity: {parseFloat(`${cell.row.original.unfulfilledQuantity}`)}
			<br />
			Unassigned Quantity: {parseFloat(`${cell.row.original.unassignedQuantity}`)}
			<br />
			Awaiting Delivery Quantity: {parseFloat(`${cell.row.original.awaitingDeliveryQuantity}`)}
			<br />
			Awaiting Pickup Quantity: {parseFloat(`${cell.row.original.awaitingPickupQuantity}`)}
			<br />
			Fulfilled Quantity: {parseFloat(`${cell.row.original.fulfilledQuantity}`)}
			<br />
			On Order Quantity: {parseFloat(`${cell.row.original.onOrderQuantity}`)}
			<br />
			Partial Order Quantity: {parseFloat(`${cell.row.original.partialOrderQuantity}`)}
			<br />
			Returned Quantity: {parseFloat(`${cell.row.original.returnedQuantity}`)}
		</>;

		return (
			<DynamicTooltip tooltipContent={message}>{parseFloat(`
					${cell.row.original.unfulfilledQuantity
				+ cell.row.original.unassignedQuantity
				+ cell.row.original.awaitingDeliveryQuantity
				+ cell.row.original.awaitingPickupQuantity
				+ cell.row.original.fulfilledQuantity
				+ cell.row.original.onOrderQuantity
				+ cell.row.original.partialOrderQuantity
				+ cell.row.original.returnedQuantity
				}`)}</DynamicTooltip>
		);
	}, []);

	const columns: TableProps<W_Item_FindRelatedOrdersTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'status',
			cell: renderSelectedStatus,
			header: 'Status',
			accessor: 'status',
			enableSorting: true,
		},
		{
			id: 'operator',
			cell: (_cell) => {
				return _cell.getValue();
			},
			header: 'Operator',
			accessor: 'operator',
			enableSorting: true,
		},
		{
			id: 'deliveryMethod',
			cell: (_cell) => {
				return _cell.getValue();
			},
			header: 'Delivery Method',
			accessor: 'deliveryMethod',
			enableSorting: true,
		},
		{
			id: 'orderType',
			cell: (_cell) => {
				return _cell.getValue();
			},
			header: 'Order Type',
			accessor: 'orderType',
			enableSorting: true,
		},
		{
			id: 'dateSubmitted',
			cell: (_cell) => {
				return TimeUtils.formatDate(_cell.getValue(), TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);
			},
			header: 'Date Submitted',
			accessor: 'dateSubmitted',
			enableSorting: true,
		},
		{
			id: 'dateNeeded',
			cell: (_cell) => {
				return TimeUtils.formatDate(_cell.getValue(), TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);
			},
			header: 'Date Needed',
			accessor: 'dateNeeded',
			enableSorting: true,
		},
		{
			id: 'location',
			cell: (_cell) => {
				return _cell.getValue();
			},
			header: 'Location',
			accessor: 'location',
			enableSorting: true,
		},
		{
			id: 'quantity',
			cell: renderQuantityCell,
			header: 'Quantity',
			accessor: 'quantity',
			enableSorting: true,
		},
		{
			id: 'requestedAt',
			accessor: 'requestedAt',
			header: 'Requested At',
			cell: (cell: CellContext<W_Item_FindRelatedOrdersTable_VM_Row, string>) =>
				<UpdatedByCell updatedAt={new Date(cell.row.original.requestedAt)} updatedBy={cell.row.original.requestedBy} />,
			enableSorting: true,
			enableHiding: true,
		},
		{
			id: 'link',
			cell: renderLinkCell(),
			header: 'Order Preview',
			accessor: 'link',
		},
	], [renderLinkCell, renderQuantityCell]);

	const onDownloadCSVClick = React.useCallback(async () => {
		if (!tableRef.current) {
			return;
		}
		const tableQuery = tableRef.current.getTableQuery();
		const result = await fetchRows({ ...tableQuery, pageSize: 10000, page: 0 }); // Basically - get all
		const csvData = toCSVData(result.rows);

		const _csvName = `${companyData.name}_${item.name}_related_orders.csv`;

		downloadCSV(csvData, _csvName);
	}, [companyData.name, fetchRows, item.name]);

	const tableButtons = React.useMemo(() => {
		return [
			{
				type: TableButtonType.EXPORT,
				hasPermission: true,
				onClick: onDownloadCSVClick,
				label: 'Export as CSV',
			},
		];
	}, [onDownloadCSVClick]);

	return (
		<>
			<div className={styles.section}>
				<div className="m-b-m">
					<SegmentLabel
						label="Related Orders"
						tooltip="A filterable log of all orders associated with this item, displaying status, operator, delivery method, quantities, and key dates across all locations. Order type can be regular, which decrements inventory by the order quantity, or for stock, which increments item stock."
					/>
				</div>
				<TableNew
					additionalFilter={renderAdditionalFilter}
					buttons={tableButtons}
					columns={columns}
					defaultPageSize={100}
					fetch={fetchRows}
					hasSearchInput={true}
					ref={tableRef}
					tableName={TableNameEnum.ITEM_RELATED_ORDERS}
				/>
			</div>
		</>
	);
};

const mapStateToProps = (state: RootState) => {
	const { companyData, userData } = state.user;
	if (!companyData || !userData) {
		throw new Error('User not logged in');
	}

	return {
		companyData,
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps());

function mapDispatchToProps() {
	return {
		findForTable: ItemActions.findRelatedOrdersTable,
	};
}

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(RelatedOrdersTable);
